import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { DepositPanel } from "./DepositPanel";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TIME_SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

const toYMD = (d) => d.toLocaleDateString("en-CA");
const fmtLong = (d) =>
  d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

const isDisabledDay = (date) => {
  const day = date.getDay();
  if (day === 0 || day === 6) return true;
  const min = new Date();
  min.setHours(0, 0, 0, 0);
  min.setDate(min.getDate() + 1);
  const max = new Date();
  max.setDate(max.getDate() + 90);
  return date < min || date > max;
};

const ChoicePicker = ({ num, value, onChange }) => (
  <div className="border border-[var(--brand-gold-soft)] bg-[var(--brand-cream)] p-4">
    <p className="font-serif-brand text-lg font-semibold text-[var(--brand-navy)]">
      <span className="text-[var(--brand-gold)]">{num}.</span>{" "}
      {num === 1 ? "First choice" : "Second choice"}
    </p>
    <div className="mt-2 flex justify-center bg-white border border-[var(--brand-gold-soft)]">
      <Calendar
        mode="single"
        selected={value.date}
        onSelect={(d) => onChange({ ...value, date: d || null })}
        disabled={isDisabledDay}
        data-testid={`request-calendar-${num}`}
      />
    </div>
    <div className="mt-3 grid grid-cols-4 gap-1.5">
      {TIME_SLOTS.map((t) => (
        <button
          key={t}
          type="button"
          data-testid={`request-time-${num}-${t.replace(/[:\s]/g, "")}`}
          onClick={() => onChange({ ...value, time: t })}
          className={`px-1 py-1.5 text-xs font-medium border transition-colors ${
            value.time === t
              ? "bg-[var(--brand-navy)] border-[var(--brand-navy)] text-white"
              : "bg-white border-[var(--brand-gold-soft)] text-[var(--brand-navy)] hover:border-[var(--brand-gold)]"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
    <p className="mt-2 min-h-[1.25rem] text-xs text-[var(--brand-muted)]">
      {value.date && value.time
        ? `${fmtLong(value.date)} at ${value.time}`
        : "Pick a weekday and a time."}
    </p>
  </div>
);

export const AppointmentRequest = ({ visitType, variant }) => {
  const [c1, setC1] = useState({ date: null, time: null });
  const [c2, setC2] = useState({ date: null, time: null });
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [hp, setHp] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const complete =
    c1.date && c1.time && c2.date && c2.time &&
    form.first_name && form.last_name && form.email && form.phone;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (toYMD(c1.date) === toYMD(c2.date) && c1.time === c2.time) {
      setError("Please pick two different times.");
      return;
    }
    setState("submitting");
    try {
      const { data } = await axios.post(`${API}/appointment-requests`, {
        ...form,
        visit_type: visitType,
        choice1_date: toYMD(c1.date),
        choice1_time: c1.time,
        choice2_date: toYMD(c2.date),
        choice2_time: c2.time,
        company: hp || null,
      });
      setRequestId(data.request_id);
      setState("done");
      track(variant, "appointment_request");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again or call the office.");
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="request-success" className="mx-auto w-full max-w-xl px-4 py-10 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
        <h3 className="mt-4 font-serif-brand text-2xl font-semibold text-[var(--brand-navy)]">
          Request received &mdash; not confirmed yet
        </h3>
        <p className="mt-3 text-base leading-relaxed text-[var(--brand-muted)]">
          Thank you, {form.first_name}! Dr. Crosson&rsquo;s office will contact you to confirm one
          of your requested times. Your appointment is <span className="font-semibold text-[var(--brand-navy)]">not
          confirmed</span> until you hear from the office.
        </p>
        <div className="mt-5 border border-[var(--brand-gold-soft)] bg-[var(--brand-cream)] p-4 text-left text-sm text-[var(--brand-navy-deep)]">
          <p><span className="font-semibold">1.</span> {fmtLong(c1.date)} at {c1.time}</p>
          <p className="mt-1"><span className="font-semibold">2.</span> {fmtLong(c2.date)} at {c2.time}</p>
        </div>
        <div className="mt-6 border border-[var(--brand-gold-soft)] text-left">
          <p className="border-b border-[var(--brand-gold-soft)] bg-white px-4 pt-4 pb-3 text-sm leading-relaxed text-[var(--brand-muted)]">
            <span className="font-semibold text-[var(--brand-navy)]">Want to hold your place in line?</span>{" "}
            You can choose to pay the $25 deposit now &mdash; it&rsquo;s applied toward your visit
            and puts your request first.
          </p>
          <DepositPanel requestId={requestId} />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="appointment-request-form" className="mx-auto w-full max-w-3xl px-4 py-5">
      <p className="text-sm leading-relaxed text-[var(--brand-muted)]">
        Pick your <span className="font-semibold text-[var(--brand-navy)]">top two times</span>{" "}
        (Monday&ndash;Friday) and the office will reach out to confirm. Your appointment is not
        confirmed until you hear from the office.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <ChoicePicker num={1} value={c1} onChange={setC1} />
        <ChoicePicker num={2} value={c2} onChange={setC2} />
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Input data-testid="request-first-name" required placeholder="First name" value={form.first_name} onChange={set("first_name")} />
        <Input data-testid="request-last-name" required placeholder="Last name" value={form.last_name} onChange={set("last_name")} />
        <Input data-testid="request-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} />
        <Input data-testid="request-phone" required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} />
      </div>
      <input
        type="text"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
      />
      {error && (
        <p data-testid="request-error" className="mt-3 text-sm text-red-700" role="alert">
          {String(error)}
        </p>
      )}
      <button
        type="submit"
        data-testid="request-submit-button"
        disabled={!complete || state === "submitting"}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-[var(--brand-navy)] px-6 py-3 text-base font-medium text-white transition-colors hover:bg-[var(--brand-navy-deep)] disabled:opacity-50"
      >
        {state === "submitting" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Request these times
      </button>
      <p className="mt-2 pb-2 text-center text-xs text-[var(--brand-muted)]">
        No payment required to request times. The office will confirm by phone or email.
      </p>
    </form>
  );
};
